import { Logger, Utils } from "neo-common";
import { AnimationFrameEventEmitter } from "../AnimationFrame/AnimationFrame";
import { FrameTimerPool } from "./FrameTimerPool";
import { StageController } from "../Components/StageController";

export const FrameTimerEvents = {
    Progress: "progress",
    Complete: "complete",
};

/**
 * FrameTimer is class to count time in frames.
 * FrameTimer is used instead of window.interval or window.timeout.
 * FrameTimer doesnt use miliseconds but count with frames.
 * @class FrameTimer
 */
export class FrameTimer extends AnimationFrameEventEmitter {
    private _timerCounter: number;
    private _finalTimerCounter: number;
    private _timerName: string;
    private _useFrames: boolean = false;
    private _timer: number;
    private _playing: boolean = false;
    private _paused: boolean = false;

    private _uid: string;

    private _startTime: number;

    constructor(timerName: string) {
        super();

        timerName = Utils.valueOrDefault(timerName, Utils.generateUuid());

        this._timerName = `FrameTimer-${timerName}`;

        this._uid = `FrameTimer_${timerName}_${Utils.generateUuid()}`;

        // default values
        this._timerCounter = 0;
        this._finalTimerCounter = 0;
    }

    protected frame_onFrame(): void {
        if (this._timerCounter === this._finalTimerCounter) {
            this.stop();
            this.conclude();
        } else {
            this.super2.emit(FrameTimerEvents.Progress);
            this._timerCounter++;
        }
    }

    protected get frame_name(): string {
        return this._timerName;
    }

    /**
     * Set counter to zero and set duration for counter.
     * Start counting.
     * @method start
     * @param duration - number (frames)
     * @return void
     */
    start(duration: number, ...args: any[]): void {
        if (!this._playing) {
            FrameTimerPool.add(this._uid, this);
        }
        this._playing = true;
        if (this._useFrames) {
            this._timerCounter = 0;
            this._finalTimerCounter = duration;
            this.frame_stop();

            // start counting
            this.count();
        } else {
            /*
            if(this._timer != null) {
                clearTimeout(this._timer);
            }
            */
            // finalTimerCounter is target time
            this._startTime = new Date().getTime();
            this._finalTimerCounter = duration * 16.7;

            // TODO method
            this._timer = setTimeout(() => this.conclude(), this._finalTimerCounter);

            if (FrameTimerPool.paused) {
                this.pause();
            }
        }
    }

    /**
     * Stop counting.
     * @method stop
     * @return void
     */
    stop(): void {
        if (this._playing) {
            FrameTimerPool.remove(this._uid);
        }
        this._playing = false;
        if (this._useFrames) {
            this.frame_stop();
        } else {
            if (this._timer != null) {
                clearTimeout(this._timer);
            }
        }
    }

    private conclude(...args: any[]): void {
        clearTimeout(this._timer);
        FrameTimerPool.remove(this._uid);
        this._playing = false;
        this.super2.emit(FrameTimerEvents.Complete, ...args);
    }

    getRemainingTime(): number {
        if (!this._playing) {
            return null;
        }
        return this._paused
            ? this._finalTimerCounter
            : (this._finalTimerCounter - +new Date()) * (this._useFrames ? 1 : StageController.FPS / 1000);
    }

    pause(): void {
        if (!this._playing || this._paused) {
            return;
        }
        if (this._useFrames) {
            this.frame_stop();
        } else {
            // frameTimerCounter becomes remaining time
            clearTimeout(this._timer);
            let currentTime = new Date().getTime();
            this._finalTimerCounter -= currentTime - this._startTime;
        }
        this._paused = true;
    }

    isPlaying(): boolean {
        return this._playing;
    }

    unpause(): void {
        if (!this._playing || !this._paused) {
            return;
        }
        if (this._useFrames) {
            this.count();
        } else {
            this._startTime = new Date().getTime();
            window.clearTimeout(this._timer);
            this._timer = setTimeout(() => this.conclude(), this._finalTimerCounter);
        }
        this._paused = false;
    }

    /**
     * Count method emit Complete and Progress event while counting with requesrtAnimationFrame.
     * @method count
     * @retutn void
     */
    count(): void {
        this.frame_start();
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @retutn void
     */
    log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup("FrameTimer", s);
        }
    }
}

export type FrameTimerEventEmitter = FrameTimer & PIXI.utils.EventEmitter;

export const CreateFrameTimer = (timerName: string): FrameTimerEventEmitter => {
    return new FrameTimer(timerName) as FrameTimerEventEmitter;
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Utils/FrameTimer.ts
 **/